import { Component, computed, effect, input, output, signal, viewChild, AfterViewInit, OnDestroy, ElementRef, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastService } from 'src/app/services/toast.service';

export interface LoanTableSignalsItem {
  readonly id: string;
  loan: string;
  isEditing?: boolean;
  isDuplicate?: boolean;
  isSelected?: boolean;
}

@Component({
  selector: 'app-loan-number-table',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatCardModule,
    MatTooltipModule,
    MatChipsModule,
    MatDividerModule,
    MatMenuModule,
    MatCheckboxModule,
  ],
  templateUrl: './loan-number-table.component.html',
  styleUrl: './loan-number-table.component.scss',
  standalone: true,
})
export class LoanNumberTableComponent implements AfterViewInit, OnDestroy {
  // Dependency injection
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);

  //#region Input_Output
  readonly maxLoans = input<number>(300);
  readonly pageSize = input<number>(10);
  readonly pageSizeOptions = input<number[]>([10, 25, 50, 100]);
  readonly loansChange = output<string[]>();
  //#endregion

  //#region ViewChild signals
  readonly sort = viewChild<MatSort>('tableSort');
  readonly paginator = viewChild(MatPaginator);
  readonly pasteArea = viewChild<ElementRef<HTMLElement>>('pasteArea');
  //#endregion

  //#region Internal signals
  private readonly loansSignal = signal<LoanTableSignalsItem[]>([]);
  private readonly editingItemSignal = signal<LoanTableSignalsItem | null>(null);
  private readonly selectionSignal = signal<LoanTableSignalsItem[]>([]);
  private readonly removeDuplicatesOnPasteSignal = signal<boolean>(true);
  //#endregion
  private lastSelectedIndex = -1;
  
  // Mat Table setup
  readonly dataSource = new MatTableDataSource<LoanTableSignalsItem>([]);
  readonly displayedColumns: string[] = ['select', 'loan', 'actions'];
  readonly selection = new SelectionModel<LoanTableSignalsItem>(true, []);

  // Form control for editing with improved validation
  readonly editFormControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(100), // Reasonable max length
  ]);

  //#region Computed values
  readonly totalCount = computed(() => this.loansSignal().length);
  readonly uniqueCount = computed(() => {
    const uniqueLoans = new Set(
      this.loansSignal()
        .map((item) => item.loan.trim().toLowerCase())
        .filter((loan) => loan.length > 0),
    );
    return uniqueLoans.size;
  });
  readonly duplicateCount = computed(() => this.totalCount() - this.uniqueCount());
  readonly isOverMaxLoans = computed(() => this.totalCount() > this.maxLoans());
  readonly hasSelection = computed(() => this.selectionSignal().length > 0);
  readonly selectionCount = computed(() => this.selectionSignal().length);
  readonly hasEditingItem = computed(() => this.editingItemSignal() !== null);
  readonly removeDuplicatesOnPaste = computed(() => this.removeDuplicatesOnPasteSignal());

  private readonly processedLoans = computed(() => {
    const loans = this.loansSignal();
    const loanCounts = new Map<string, number>();

    // Count occurrences of normalized loan numbers
    loans.forEach((item) => {
      const normalizedLoan = item.loan.trim().toLowerCase();
      if (normalizedLoan.length > 0) {
        loanCounts.set(normalizedLoan, (loanCounts.get(normalizedLoan) || 0) + 1);
      }
    });

    // Mark duplicates
    return loans.map((item) => ({
      ...item,
      isDuplicate: (loanCounts.get(item.loan.trim().toLowerCase()) || 0) > 1,
    }));
  });
  //#endregion

  constructor() {
    // Effect to update data source and emit changes
    effect(() => {
      const processedData = this.processedLoans();
      this.dataSource.data = [...processedData];

      this.updateSelectionModel(processedData);

      const validLoans = processedData
        .map((item) => item.loan.trim())
        .filter((loan) => loan.length > 0);

      this.loansChange.emit(validLoans);
    });

    // Track selection changes and sync with signal
    this.selection.changed.pipe(
      passUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.selectionSignal.set([...this.selection.selected]);
    });
  }

  ngAfterViewInit(): void {
    const sortElement = this.sort();
    const paginatorElement = this.paginator();

    if (sortElement) {
      this.dataSource.sort = sortElement;
      this.setupCustomSorting();
    }

    if (paginatorElement) {
      this.dataSource.paginator = paginatorElement;
    }
  }

  ngOnDestroy(): void {
    this.selection.clear();
    this.editingItemSignal.set(null);
  }

  //#region init
  private updateSelectionModel(currentData: LoanTableSignalsItem[]): void {
    // Get currently selected IDs
    const selectedIds = new Set(this.selection.selected.map(item => item.id));
    
    // Clear and re-select items that still exist
    this.selection.clear();
    const itemsToSelect = currentData.filter(item => selectedIds.has(item.id));
    this.selection.select(...itemsToSelect);
  }

  private setupCustomSorting(): void {
    if (!this.dataSource.sort) {
      return;
    }

    // Custom sort function - handles duplicates properly
    this.dataSource.sort.sortChange
    .pipe(passUntilDestroyed(this.destroyRef))
    .subscribe((sortState) => {
      if (this.dataSource.sort?.active === 'loan') {
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(() => this.sortLoanData());
        } else {
          this.sortLoanData();
        }
      }
    });

    // Set up the basic sorting data accessor
    this.dataSource.sortingDataAccessor = (data: LoanTableSignalsItem, sortHeaderId: string): string | number => {
      switch (sortHeaderId) {
        case 'loan':
          return data.loan.toLowerCase().trim();
        default:
          return '';
      }
    };
  }

  private sortLoanData(): void {
    const direction = this.dataSource.sort!.direction;
    if (direction === '') {
      return;
    }

    const sortedData = [...this.dataSource.data].sort((a, b) => {
      const loanA = a.loan.trim();
      const loanB = b.loan.trim();

      // Parse as numbers if possible
      const numA = parseFloat(loanA);
      const numB = parseFloat(loanB);

      let comparison = 0;

      // If both are valid numbers, compare numerically
      if (!isNaN(numA) && !isNaN(numB) && isFinite(numA) && isFinite(numB)) {
        comparison = numA - numB;
      } else {
        // Otherwise compare as strings (case-insensitive)
        comparison = loanA.toLowerCase().localeCompare(loanB.toLowerCase());
      }

      // If loan numbers are the same, sort by ID for consistent ordering of duplicates
      if (comparison === 0) {
        comparison = a.id.localeCompare(b.id);
      }

      // Apply sort direction
      return direction === 'asc' ? comparison : -comparison;
    });

    // Update the data source
    this.dataSource.data = sortedData;
  }
  //#endregion

  //#region Template helper methods
  getLoanCellClasses(row: LoanTableSignalsItem): string {
    const classes: string[] = [];
    if (row.isDuplicate) classes.push('duplicate');
    return classes.join(' ');
  }

  getRowClasses(row: LoanTableSignalsItem): string {
    const classes: string[] = [];
    if (row.isEditing) classes.push('editing-row');
    if (row.isDuplicate) classes.push('duplicate-row');
    if (this.selection.isSelected(row)) classes.push('selected-row');
    return classes.join(' ');
  }
  //#endregion

  //#region Paste handling
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const data = event.clipboardData?.getData('text') || '';
    if (data.trim()) {
      this.processPastedData(data);
    }
  }

  private processPastedData(data: string): void {
    const cleanedRows = this.parseDelimitedData(data);

    if (cleanedRows.length === 0) {
      this.toastService.toastCreate('No valid loan numbers found in pasted data', 'Warning');
      return;
    }

    // Filter duplicates if the option is enabled
    let filteredRows = cleanedRows;
    let duplicatesRemovedCount = 0;
    
    if (this.removeDuplicatesOnPaste()) {
      const existingLoansSet = new Set(
        this.loansSignal().map(item => item.loan.trim().toLowerCase()),
      );
      
      const uniqueRows: string[] = [];
      const seenInPaste = new Set<string>();
      
      cleanedRows.forEach(loan => {
        const normalizedLoan = loan.trim().toLowerCase();
        if (!existingLoansSet.has(normalizedLoan) && !seenInPaste.has(normalizedLoan)) {
          uniqueRows.push(loan);
          seenInPaste.add(normalizedLoan);
        }
      });
      
      duplicatesRemovedCount = cleanedRows.length - uniqueRows.length;
      filteredRows = uniqueRows;
    }

    if (filteredRows.length === 0) {
      this.toastService.toastCreate('All pasted loan numbers were duplicates', 'Warning');
      return;
    }

    const currentCount = this.totalCount();
    const maxAllowed = this.maxLoans();

    // Add all loans, but show warnings if we go over the limit
    this.addLoansToSignal(filteredRows);

    const newCount = currentCount + filteredRows.length;
    const overLimit = newCount - maxAllowed;

    // Build comprehensive message
    let message = `Added ${filteredRows.length} loan numbers`;
    const messageParts: string[] = [];

    if (duplicatesRemovedCount > 0) {
      messageParts.push(`${duplicatesRemovedCount} duplicates removed`);
    }

    if (overLimit > 0) {
      messageParts.push(`${overLimit} loans over the recommended limit of ${maxAllowed}`);
    }
    if (messageParts.length > 0) {
      message += ` (${messageParts.join(', ')})`;
    }
    
    // Show warning if over limit, otherwise success
    const toastType = overLimit > 0 ? 'Warning' : (duplicatesRemovedCount > 0 ? 'Warning' : 'Success');
    this.toastService.toastCreate(message, toastType);
  }

  private parseDelimitedData(data: string): string[] {
    // Handle different delimiters
    const delimiters = [',', '|', '\t', ' ', '\n', ';'];
    let bestDelimiter = '\n';
    let maxSplits = 0;

    // Find the delimiter that produces the most splits
    for (const delimiter of delimiters) {
      const splits = data.split(delimiter)
        .map(item => item.trim())
        .filter(item => item.length > 0);
      
      if (splits.length > maxSplits) {
        maxSplits = splits.length;
        bestDelimiter = delimiter;
      }
    }

    return data
      .split(bestDelimiter)
      .map((loan) => loan.trim().replace(/["\r\n]/g, ''))
      .filter((loan) => loan.length > 0)
      .filter((loan) => !this.isHeaderValue(loan))
      .slice(0, 1000); // Prevent excessive data processing
  }

  private isHeaderValue(value: string): boolean {
    const headerValues = ['loan', 'loans', 'loan:', 'loans:', 'loan number', 'loan numbers'];
    return headerValues.includes(value.toLowerCase());
  }
  //#endregion

  private addLoansToSignal(loans: string[]): void {
    const newLoanItems: LoanTableSignalsItem[] = loans.map((loan) => ({
      id: this.generateId(),
      loan: loan,
      isEditing: false,
      isDuplicate: false,
    }));

    this.loansSignal.update((currentLoans) => [...currentLoans, ...newLoanItems]);
  }

  //#region CRUD operations
  addNewLoan(): void {
    if (this.totalCount() >= this.maxLoans()) {
      const overCount = this.totalCount() + 1 - this.maxLoans();
      this.toastService.toastCreate(
        `Adding loan will exceed recommended limit by ${overCount}`,
        'Warning',
      );
    }

    // Cancel any existing edit first
    this.cancelCurrentEdit();

    const newLoan: LoanTableSignalsItem = {
      id: this.generateId(),
      loan: '',
      isEditing: true,
      isDuplicate: false,
    };

    // Add to signal and immediately start editing
    this.loansSignal.update((loans) => [...loans, newLoan]);
    this.editingItemSignal.set(newLoan);
    this.editFormControl.setValue('');

    setTimeout(() => this.focusEditInput(), 0);
  }

  startEdit(item: LoanTableSignalsItem): void {
    // Cancel any existing edit
    this.cancelCurrentEdit();

    this.editingItemSignal.set(item);
    this.editFormControl.setValue(item.loan);

    this.updateLoanEditingState(item.id, true);
    setTimeout(() => this.focusEditInput(), 0);
  }

  saveEdit(item: LoanTableSignalsItem): void {
    if (!this.editFormControl.valid) {
      this.toastService.toastCreate('Please enter a valid loan number', 'Warning');
      return;
    }

    const newValue = this.editFormControl.value?.trim() || '';

    if (!newValue) {
      this.removeLoan(item.id);
      return;
    }

    // Check for duplicates if the option is enabled and this is a new loan (was empty)
    if (this.removeDuplicatesOnPaste() && item.loan === '') {
      const normalizedNewValue = newValue.toLowerCase();
      const isDuplicate = this.loansSignal().some(loan => 
        loan.id !== item.id && loan.loan.trim().toLowerCase() === normalizedNewValue,
      );
      
      if (isDuplicate) {
        this.toastService.toastCreate(`Duplicate loan number "${newValue}" was not added`, 'Warning');
        this.removeLoan(item.id);
        return;
      }
    }

    // Update the loan value and clear editing state
    this.loansSignal.update((loans) =>
      loans.map((loan) =>
        loan.id === item.id
          ? { ...loan, loan: newValue, isEditing: false }
          : loan,
      ),
    );

    this.clearEditingControls();
    this.toastService.toastCreate('Loan number saved successfully', 'Success');
  }

  cancelEdit(item: LoanTableSignalsItem): void {
    // If this is a new loan (empty loan value), remove it
    if (item.loan === '') {
      this.removeLoan(item.id);
    } else {
      this.updateLoanEditingState(item.id, false);
    }

    this.clearEditingControls();
  }

  private cancelCurrentEdit(): void {
    const currentEditingItem = this.editingItemSignal();
    if (currentEditingItem) {
      this.cancelEdit(currentEditingItem);
    }
  }

  private updateLoanEditingState(id: string, isEditing: boolean): void {
    this.loansSignal.update((loans) =>
      loans.map(
        (loan) =>
          loan.id === id
            ? { ...loan, isEditing }
            : { ...loan, isEditing: false }, // Ensure only one item is editing
      ),
    );
  }

  private focusEditInput(): void {
    const input = document.querySelector('.editing-row mat-form-field input') as HTMLInputElement;
    if (input) {
      input.focus();
      input.select(); // Select all text for easy replacement
    }
  }

  removeLoan(id: string): void {
    // Clear editing state if removing the currently editing item
    const editingItem = this.editingItemSignal();
    if (editingItem?.id === id) {
      this.clearEditingControls();
    }

    // Remove from loans signal
    this.loansSignal.update(loans => loans.filter(loan => loan.id !== id));
    
    // Update selection to remove the deleted item
    const itemToRemove = this.selection.selected.find(item => item.id === id);
    if (itemToRemove) {
      this.selection.deselect(itemToRemove);
    }
  }

  clearAll(): void {
    this.loansSignal.set([]);
    this.clearEditingControls();
    this.clearSelection();
  }
  //#endregion

  //#region Selection methods
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected > 0 && numSelected === numRows;
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.clearSelection();
    } else {
      this.selection.select(...this.dataSource.data);
      this.lastSelectedIndex = this.dataSource.data.length - 1;
    }
  }

  selectAll(): void {
    this.selection.select(...this.dataSource.data);
    this.lastSelectedIndex = this.dataSource.data.length - 1;
  }

  public clearSelection(): void {
    this.selection.clear();
    this.lastSelectedIndex = -1;
  }

  onRowClick(row: LoanTableSignalsItem, event: MouseEvent): void {
    // Prevent selection during editing
    if (row.isEditing) {
      return;
    }

    this.handleSelection(row, event);
  }

  onCheckboxClick(row: LoanTableSignalsItem, event: MouseEvent): void {
    // Stop event propagation to prevent row click
    event.stopPropagation();
    
    // Handle the selection logic
    this.handleSelection(row, event);
  }

  private handleSelection(row: LoanTableSignalsItem, event: MouseEvent): void {
    const currentIndex = this.dataSource.data.findIndex(item => item.id === row.id);
    
    if (event.shiftKey && this.lastSelectedIndex !== -1) {
      // Shift+Click: Range selection
      this.selectRange(this.lastSelectedIndex, currentIndex);
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl+Click (Cmd+Click on Mac): Toggle individual selection
      this.selection.toggle(row);
      this.lastSelectedIndex = currentIndex;
    } else {
      // Regular click: Single selection (for row clicks) or toggle (for checkbox clicks)
      if (event.target && (event.target as HTMLElement).closest('mat-checkbox')) {
        // If it's a checkbox click without modifiers, just toggle
        this.selection.toggle(row);
      } else {
        // If it's a row click, clear others and select this one
        this.selection.clear();
        this.selection.select(row);
      }
      this.lastSelectedIndex = currentIndex;
    }
  }

  private selectRange(startIndex: number, endIndex: number): void {
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);
    
    // Clear current selection
    this.selection.clear();
    
    // Select range
    for (let i = start; i <= end; i++) {
      if (i < this.dataSource.data.length) {
        this.selection.select(this.dataSource.data[i]);
      }
    }
  }

  removeSelected(): void {
    const selectedIds = this.selection.selected.map((item) => item.id);

    // Clear editing state if removing the currently editing item
    const editingItem = this.editingItemSignal();
    if (editingItem && selectedIds.includes(editingItem.id)) {
      this.clearEditingControls();
    }

    this.loansSignal.update((loans) =>
      loans.filter((loan) => !selectedIds.includes(loan.id)),
    );
    this.clearSelection();

    this.toastService.toastCreate(`Removed ${selectedIds.length} loan numbers`, 'Success');
  }
  //#endregion

  removeDuplicates(): void {
    const uniqueLoans = new Map<string, LoanTableSignalsItem>();

    this.loansSignal().forEach((item) => {
      const key = item.loan.trim().toLowerCase();
      if (key.length > 0 && !uniqueLoans.has(key)) {
        uniqueLoans.set(key, item);
      }
    });

    const duplicatesRemoved = this.totalCount() - uniqueLoans.size;

    if (duplicatesRemoved > 0) {
      this.loansSignal.set(Array.from(uniqueLoans.values()));
      this.clearSelection();
      this.clearEditingControls();
      this.toastService.toastCreate(
        `Removed ${duplicatesRemoved} duplicate loan numbers`,
        'Success',
      );
    }
  }

  private clearEditingControls(): void {
    this.editingItemSignal.set(null);
    this.editFormControl.setValue('');
  }

  // Duplicate removal toggle
  toggleRemoveDuplicatesOnPaste(checked: boolean): void {
    this.removeDuplicatesOnPasteSignal.set(checked);
  }

  // Utility methods

  private generateId(): string {
    return `loan-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
}
